import type * as React from 'react';

import { Button } from './button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

type ButtonProps = React.ComponentProps<typeof Button>;
type IconButtonSize = Extract<
  NonNullable<ButtonProps['size']>,
  'icon' | 'icon-xs' | 'icon-sm' | 'icon-lg'
>;

interface IconButtonProps extends Omit<ButtonProps, 'size'> {
  size?: IconButtonSize;
  tooltip?: React.ReactNode;
  tooltipContentProps?: React.ComponentProps<typeof TooltipContent>;
  tooltipProps?: React.ComponentProps<typeof Tooltip>;
  tooltipProviderProps?: React.ComponentProps<typeof TooltipProvider>;
}

function IconButton({
  size = 'icon',
  tooltip,
  tooltipContentProps,
  tooltipProps,
  tooltipProviderProps,
  ...buttonProps
}: IconButtonProps) {
  const accessibleLabel =
    buttonProps['aria-label'] ??
    (typeof tooltip === 'string' ? tooltip : undefined);

  const button = (
    <Button {...buttonProps} aria-label={accessibleLabel} size={size} />
  );

  if (!tooltip) {
    return button;
  }

  return (
    <TooltipProvider {...tooltipProviderProps}>
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent sideOffset={8} {...tooltipContentProps}>
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export { IconButton };
